import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import axios from 'axios'
import {
    Armchair,
    Check,
    CirclePlus,
    Globe2,
    LockKeyhole,
    Pencil,
    Plus,
    Trash2,
} from 'lucide-react'
import {
    Badge,
    Button,
    DataTable,
    ErrorState,
    FormField,
    IconButton,
    Modal,
    SelectInput,
    Spinner,
    StatusPill,
    TextInput,
} from '../ui'
import { toast } from 'react-toastify'
import {
    listTables,
    createTable,
    updateTable,
    deleteTable,
} from '../actions/tableActions'
import {
    TABLE_CREATE_RESET,
    TABLE_UPDATE_RESET,
    TABLE_DELETE_RESET,
} from '../constants/tableConstants'
import { useDialog } from './DialogProvider';

let API_URL = process.env.REACT_APP_API_URL || 'https://localhost:5000'
if (API_URL.endsWith('/api')) API_URL = API_URL.slice(0, -4)
if (API_URL.endsWith('/')) API_URL = API_URL.slice(0, -1)

const getEmptyFormData = () => ({
    tableName: '',
    tableNumber: '', // Auto-generated usually, but can be shown
    location: '',
    hourlyPrice: '',
    assignedDealers: [],
    gameType: 'Texas Holdem',
    totalSeats: 9,
    smallBlind: 0,
    bigBlind: 0,
    minBuyIn: 0,
    maxBuyIn: 0,
    status: 'Active',
})

const formatHourlyPrice = (value) =>
    new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR',
        maximumFractionDigits: 2,
    }).format(Number(value) || 0)

const PRIVATE_HOUR_PRESETS = [1, 2, 3, 4, 6, 8]
const TABLE_STATUSES = ['Active', 'Inactive', 'Maintenance']
const GAME_TYPES = ['Texas Holdem', 'Omaha', 'Mixed Games']
const MAX_PRIVATE_HOURS = 24
const MIN_TABLE_SEATS = 2
const MAX_TABLE_SEATS = 50

// A private booking with an end time lapses on its own; legacy ones without an end time stay private
const isPrivateActive = (table) =>
    Boolean(table.isPrivate) &&
    (!table.privateUntil || new Date(table.privateUntil) > new Date())

const formatDateTime = (value) =>
    new Date(value).toLocaleString('en-IN', {
        day: 'numeric',
        month: 'short',
        hour: 'numeric',
        minute: '2-digit',
    })

const TableManagement = ({ clubId, open, onClose }) => {
    const { confirm } = useDialog();
    const dispatch = useDispatch()

    const [dialogOpen, setDialogOpen] = useState(false)
    const [editMode, setEditMode] = useState(false)
    const [selectedTable, setSelectedTable] = useState(null)
    const [statusFilter, setStatusFilter] = useState('All')
    const [formData, setFormData] = useState(getEmptyFormData)
    const [availableDealers, setAvailableDealers] = useState([])
    const [dealersLoading, setDealersLoading] = useState(false)
    const [dealersLoaded, setDealersLoaded] = useState(false)
    const [privateTable, setPrivateTable] = useState(null)
    const [privateHours, setPrivateHours] = useState('1')

    const userLogin = useSelector((state) => state.userLogin)
    const adminLogin = useSelector((state) => state.adminLogin)
    const authToken = userLogin?.userInfo?.token || adminLogin?.userInfo?.token

    // Redux State
    const tableList = useSelector((state) => state.tableList)
    const { loading, error, tables } = tableList

    const tableCreate = useSelector((state) => state.tableCreate)
    const { success: successCreate, error: errorCreate } = tableCreate

    const tableUpdate = useSelector((state) => state.tableUpdate)
    const { success: successUpdate, error: errorUpdate } = tableUpdate

    const tableDelete = useSelector((state) => state.tableDelete)
    const { success: successDelete, error: errorDelete } = tableDelete

    useEffect(() => {
        if (open && clubId) {
            dispatch(listTables({ clubId }))
        }
    }, [dispatch, open, clubId, successCreate, successUpdate, successDelete])

    // Load the club's dealers each time the add/edit form opens
    useEffect(() => {
        if (!dialogOpen || !clubId) return undefined

        let cancelled = false
        setDealersLoading(true)
        setDealersLoaded(false)
        axios
            .get(`${API_URL}/api/tables/dealers`, {
                params: { clubId },
                headers: { Authorization: `Bearer ${authToken}` },
            })
            .then(({ data }) => {
                if (!cancelled) {
                    setAvailableDealers(data?.data || [])
                    setDealersLoaded(true)
                }
            })
            .catch((requestError) => {
                if (!cancelled) {
                    setAvailableDealers([])
                    toast.error(
                        requestError.response?.data?.message || 'Failed to load dealers'
                    )
                }
            })
            .finally(() => {
                if (!cancelled) setDealersLoading(false)
            })

        return () => {
            cancelled = true
        }
    }, [dialogOpen, clubId, authToken])

    useEffect(() => {
        if (successCreate) {
            toast.success('Table created successfully')
            handleCloseDialog()
            dispatch({ type: TABLE_CREATE_RESET })
        }
        if (errorCreate) toast.error(errorCreate)

        if (successUpdate) {
            toast.success('Table updated successfully')
            handleCloseDialog()
            dispatch({ type: TABLE_UPDATE_RESET })
        }
        if (errorUpdate) toast.error(errorUpdate)

        if (successDelete) {
            toast.success('Table deleted successfully')
            dispatch({ type: TABLE_DELETE_RESET })
        }
        if (errorDelete) toast.error(errorDelete)
    }, [
        successCreate,
        errorCreate,
        successUpdate,
        errorUpdate,
        successDelete,
        errorDelete,
        dispatch,
    ])

    const handleOpenDialog = (table = null) => {
        if (table) {
            setEditMode(true)
            setSelectedTable(table)
            setFormData({
                tableName: table.tableName,
                tableNumber: table.tableNumber,
                location: table.location || '',
                hourlyPrice: table.hourlyPrice ?? 0,
                assignedDealers: (table.assignedDealers || []).map((dealer) => dealer._id || dealer),
                gameType: table.gameType || 'Texas Holdem',
                totalSeats: table.totalSeats || 9,
                smallBlind: table.stakes?.smallBlind || 0,
                bigBlind: table.stakes?.bigBlind || 0,
                minBuyIn: table.minBuyIn || 0,
                maxBuyIn: table.maxBuyIn || 0,
                status: table.status || 'Active',
            })
        } else {
            setEditMode(false)
            setSelectedTable(null)
            setFormData(getEmptyFormData())
        }
        setDialogOpen(true)
    }

    const handleCloseDialog = () => {
        setDialogOpen(false)
        setEditMode(false)
        setSelectedTable(null)
    }

    const handleSubmit = () => {
        const totalSeats = Number(formData.totalSeats)
        if (
            !Number.isInteger(totalSeats) ||
            totalSeats < MIN_TABLE_SEATS ||
            totalSeats > MAX_TABLE_SEATS
        ) {
            toast.error(
                `Total seats must be a whole number between ${MIN_TABLE_SEATS} and ${MAX_TABLE_SEATS}`
            )
            return
        }

        const tableData = {
            ...formData,
            tableName: formData.tableName.trim(),
            location: formData.location.trim(),
            hourlyPrice: parseFloat(formData.hourlyPrice),
            // Drop dealers who are no longer dealers of this club; the server rejects them
            assignedDealers: formData.assignedDealers.filter((id) =>
                availableDealers.some((dealer) => dealer._id === id)
            ),
            clubId,
            stakes: {
                smallBlind: parseFloat(formData.smallBlind),
                bigBlind: parseFloat(formData.bigBlind),
            },
            minBuyIn: parseFloat(formData.minBuyIn),
            maxBuyIn: parseFloat(formData.maxBuyIn),
            totalSeats,
        }

        if (editMode && selectedTable) {
            dispatch(updateTable({ ...tableData, _id: selectedTable._id }))
        } else {
            dispatch(createTable(tableData))
        }
    }

    const handleDelete = async (id) => {
        if (await confirm({ title: 'Delete table', message: 'Are you sure you want to delete this table?', confirmText: 'Delete', danger: true })) {
            dispatch(deleteTable(id))
        }
    }

    const handleTogglePrivate = (table) => {
        if (isPrivateActive(table)) {
            dispatch(updateTable({ _id: table._id, isPrivate: false }))
        } else {
            setPrivateHours('1')
            setPrivateTable(table)
        }
    }

    const handleClosePrivateDialog = () => setPrivateTable(null)

    const parsedPrivateHours = parseFloat(privateHours)
    const validPrivateHours =
        parsedPrivateHours > 0 && parsedPrivateHours <= MAX_PRIVATE_HOURS
    const privatePrice = validPrivateHours
        ? parsedPrivateHours * (Number(privateTable?.hourlyPrice) || 0)
        : 0

    const handleConfirmPrivate = () => {
        dispatch(
            updateTable({
                _id: privateTable._id,
                isPrivate: true,
                privateHours: parsedPrivateHours,
            })
        )
        handleClosePrivateDialog()
    }

    const filteredTables = (tables || []).filter((table) => {
        if (statusFilter === 'All') return true
        return table.status === statusFilter
    })

    // Add or drop a dealer; a dealer already on another table of this club is refused
    const handleToggleDealer = (dealer) => {
        if (formData.assignedDealers.includes(dealer._id)) {
            setFormData({
                ...formData,
                assignedDealers: formData.assignedDealers.filter((id) => id !== dealer._id),
            })
            return
        }
        const conflictTable = (tables || []).find(
            (t) =>
                String(t._id) !== String(selectedTable?._id) &&
                (t.assignedDealers || []).some((d) => (d._id || d) === dealer._id)
        )
        if (conflictTable) {
            toast.error(
                `${dealer.name || dealer.email || 'This dealer'} is already assigned to table ${conflictTable.tableName} (${conflictTable.tableNumber})`
            )
            return
        }
        setFormData({ ...formData, assignedDealers: [...formData.assignedDealers, dealer._id] })
    }

    // Esc reaches every open modal; keep the manager open while a sub-dialog is on top
    const handleCloseManager = () => {
        if (dialogOpen || privateTable) return
        onClose()
    }

    const seatsValue = Number(formData.totalSeats)
    const seatsInvalid =
        formData.totalSeats !== '' &&
        (!Number.isInteger(seatsValue) || seatsValue < MIN_TABLE_SEATS || seatsValue > MAX_TABLE_SEATS)

    const columns = [
        {
            field: 'tableNumber',
            title: 'Table',
            render: (table) => (
                <div className="min-w-0">
                    <div className="font-semibold text-cm-text">{table.tableName}</div>
                    <div className="text-xs text-cm-muted font-mono">{table.tableNumber}</div>
                </div>
            ),
        },
        {
            field: 'location',
            title: 'Location',
            render: (table) => table.location || '—',
        },
        {
            field: 'gameType',
            title: 'Game',
            render: (table) => (
                <div>
                    <div>{table.gameType}</div>
                    <div className="text-xs text-cm-muted tabular-nums">
                        Blinds {table.stakes?.smallBlind ?? 0} / {table.stakes?.bigBlind ?? 0}
                    </div>
                </div>
            ),
        },
        {
            field: 'occupiedSeats',
            title: 'Seats',
            render: (table) => (
                <Badge tone={table.occupiedSeats > 0 ? 'gold' : 'neutral'}>
                    <span className="tabular-nums">{table.occupiedSeats || 0} / {table.totalSeats}</span>
                </Badge>
            ),
        },
        {
            field: 'minBuyIn',
            title: 'Buy-in',
            render: (table) => (
                <span className="tabular-nums whitespace-nowrap">
                    {formatHourlyPrice(table.minBuyIn)} – {formatHourlyPrice(table.maxBuyIn)}
                </span>
            ),
        },
        {
            field: 'hourlyPrice',
            title: 'Price',
            render: (table) => (
                <span className="tabular-nums whitespace-nowrap">{formatHourlyPrice(table.hourlyPrice)}/hr</span>
            ),
        },
        {
            field: 'assignedDealers',
            title: 'Dealers',
            sortable: false,
            searchable: false,
            render: (table) =>
                (table.assignedDealers || []).length === 0 ? (
                    <span className="text-cm-muted">—</span>
                ) : (
                    <div className="flex flex-wrap gap-1 max-w-[220px]">
                        {table.assignedDealers.map((dealer) => {
                            const isLoggedIn = table.activeDealer?.dealer?._id === dealer._id
                            return (
                                <span key={dealer._id} title={isLoggedIn ? 'Currently logged in' : undefined}>
                                    <Badge tone={isLoggedIn ? 'success' : 'neutral'}>
                                        {isLoggedIn && (
                                            <span className="w-1.5 h-1.5 rounded-full bg-current mr-1" aria-hidden="true" />
                                        )}
                                        {dealer.name || dealer.email}
                                    </Badge>
                                </span>
                            )
                        })}
                    </div>
                ),
        },
        {
            field: 'status',
            title: 'Status',
            render: (table) => (
                <StatusPill
                    status={table.status}
                    tone={table.status === 'Active' ? 'success' : table.status === 'Maintenance' ? 'warning' : 'danger'}
                    size="sm"
                />
            ),
        },
        {
            field: 'isPrivate',
            title: 'Private',
            sortable: false,
            searchable: false,
            render: (table) => {
                const isPrivate = isPrivateActive(table)
                return (
                    <div className="flex flex-col items-start gap-1">
                        <Button
                            size="xs"
                            variant={isPrivate ? 'outline' : 'secondary'}
                            icon={isPrivate ? LockKeyhole : Globe2}
                            onClick={() => handleTogglePrivate(table)}
                        >
                            {isPrivate ? 'Private' : 'Make private'}
                        </Button>
                        {isPrivate && table.privateUntil && (
                            <span className="text-[11px] text-cm-muted whitespace-nowrap">
                                Until {formatDateTime(table.privateUntil)}
                                {table.privatePrice != null && ` · ${formatHourlyPrice(table.privatePrice)}`}
                            </span>
                        )}
                    </div>
                )
            },
        },
        {
            field: '_actions',
            title: '',
            sortable: false,
            searchable: false,
            align: 'right',
            render: (table) => (
                <div className="flex items-center justify-end gap-1">
                    <IconButton
                        icon={Pencil}
                        variant="ghost"
                        size="sm"
                        label={`Edit ${table.tableName}`}
                        onClick={() => handleOpenDialog(table)}
                    />
                    <IconButton
                        icon={Trash2}
                        variant="ghost"
                        size="sm"
                        label={`Delete ${table.tableName}`}
                        className="hover:!text-cm-danger"
                        onClick={() => handleDelete(table._id)}
                    />
                </div>
            ),
        },
    ]

    return (
        <>
            <Modal
                open={open}
                onClose={handleCloseManager}
                title="Table management"
                description="Manage tables, dealers and availability"
                icon={Armchair}
                size="full"
                bodyClassName="pt-1"
            >
                {error ? (
                    <ErrorState
                        title="Couldn't load tables"
                        description={error}
                        onRetry={() => clubId && dispatch(listTables({ clubId }))}
                    />
                ) : (
                    <DataTable
                        columns={columns}
                        data={filteredTables}
                        loading={loading}
                        searchPlaceholder="Search tables…"
                        dense
                        toolbar={
                            <SelectInput
                                size="sm"
                                className="w-40"
                                aria-label="Status filter"
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                                options={[
                                    { value: 'All', label: 'All statuses' },
                                    ...TABLE_STATUSES.map((s) => ({ value: s, label: s })),
                                ]}
                            />
                        }
                        toolbarRight={
                            <Button size="sm" icon={CirclePlus} onClick={() => handleOpenDialog()}>
                                Add table
                            </Button>
                        }
                        emptyTitle={statusFilter === 'All' ? 'No tables yet' : 'No tables match this filter'}
                        emptyDescription={statusFilter === 'All' ? 'Add the first table for this club.' : undefined}
                        emptyAction={
                            statusFilter === 'All' && (
                                <Button size="sm" icon={CirclePlus} onClick={() => handleOpenDialog()}>
                                    Add table
                                </Button>
                            )
                        }
                    />
                )}
            </Modal>

            {/* Make Private Sub-Dialog */}
            <Modal
                open={Boolean(privateTable)}
                onClose={handleClosePrivateDialog}
                title="Make table private"
                description={[
                    privateTable?.tableNumber,
                    privateTable?.tableName,
                    privateTable?.location,
                ].filter(Boolean).join(' · ')}
                icon={LockKeyhole}
                size="sm"
                footer={
                    <>
                        <Button variant="secondary" onClick={handleClosePrivateDialog}>Cancel</Button>
                        <Button icon={LockKeyhole} onClick={handleConfirmPrivate} disabled={!validPrivateHours}>
                            Make private
                        </Button>
                    </>
                }
            >
                <div className="flex flex-col gap-4">
                    <div className="flex flex-wrap gap-1.5" role="group" aria-label="Quick durations">
                        {PRIVATE_HOUR_PRESETS.map((preset) => {
                            const selected = parsedPrivateHours === preset
                            return (
                                <button
                                    key={preset}
                                    type="button"
                                    aria-pressed={selected}
                                    onClick={() => setPrivateHours(String(preset))}
                                    className={`h-8 px-3 rounded-full border text-[13px] font-semibold transition-colors ${selected
                                        ? 'border-cm-gold bg-cm-gold-soft text-cm-gold-text'
                                        : 'border-cm-border bg-cm-surface-2 text-cm-muted hover:text-cm-text'
                                        }`}
                                >
                                    {preset} hr
                                </button>
                            )
                        })}
                    </div>
                    <TextInput
                        label="Private for"
                        type="number"
                        suffix="hrs"
                        data-autofocus
                        value={privateHours}
                        onChange={(e) => setPrivateHours(e.target.value)}
                        min={0.5}
                        max={MAX_PRIVATE_HOURS}
                        step={0.5}
                        error={privateHours !== '' && !validPrivateHours
                            ? `Enter between 0 and ${MAX_PRIVATE_HOURS} hours`
                            : undefined}
                    />
                    <dl className="m-0 rounded-cm-lg border border-cm-border bg-cm-surface-2 p-3 text-sm">
                        <div className="flex justify-between gap-3">
                            <dt className="text-cm-muted">Hourly price</dt>
                            <dd className="m-0 tabular-nums">{formatHourlyPrice(privateTable?.hourlyPrice)}/hr</dd>
                        </div>
                        <div className="flex justify-between gap-3 mt-1">
                            <dt className="text-cm-muted">Duration</dt>
                            <dd className="m-0 tabular-nums">{validPrivateHours ? `${parsedPrivateHours} hr` : '—'}</dd>
                        </div>
                        <div className="flex justify-between gap-3 mt-2 pt-2 border-t border-cm-border">
                            <dt className="font-bold text-cm-text">Total price</dt>
                            <dd className="m-0 font-bold tabular-nums">{formatHourlyPrice(privatePrice)}</dd>
                        </div>
                        {validPrivateHours && (
                            <p className="text-xs text-cm-muted mt-2 mb-0">
                                Private until {formatDateTime(Date.now() + parsedPrivateHours * 60 * 60 * 1000)}
                            </p>
                        )}
                    </dl>
                </div>
            </Modal>

            {/* Add/Edit Table Sub-Dialog */}
            <Modal
                open={dialogOpen}
                onClose={handleCloseDialog}
                title={editMode ? 'Edit table' : 'Add table'}
                description={editMode ? `${selectedTable?.tableName} · ${selectedTable?.tableNumber}` : undefined}
                icon={editMode ? Pencil : CirclePlus}
                size="lg"
                footer={
                    <>
                        <Button variant="secondary" onClick={handleCloseDialog}>Cancel</Button>
                        <Button
                            onClick={handleSubmit}
                            disabled={
                                !dealersLoaded ||
                                !formData.tableName.trim() ||
                                !formData.location.trim() ||
                                !(parseFloat(formData.hourlyPrice) >= 0)
                            }
                        >
                            {editMode ? 'Save table' : 'Create table'}
                        </Button>
                    </>
                }
            >
                <div className="grid gap-4 sm:grid-cols-6">
                    <TextInput
                        className="sm:col-span-3"
                        label="Table name"
                        required
                        value={formData.tableName}
                        onChange={(e) => setFormData({ ...formData, tableName: e.target.value })}
                    />
                    <SelectInput
                        className="sm:col-span-3"
                        label="Game type"
                        value={formData.gameType}
                        onChange={(e) => setFormData({ ...formData, gameType: e.target.value })}
                        options={GAME_TYPES}
                    />
                    <TextInput
                        className="sm:col-span-3"
                        label="Location"
                        required
                        value={formData.location}
                        onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                        placeholder="e.g. Main Hall, VIP Room, 2nd Floor"
                        help="A table name can't repeat at the same location."
                    />
                    <TextInput
                        className="sm:col-span-3"
                        label="Hourly price"
                        required
                        type="number"
                        prefix="₹"
                        suffix="/hr"
                        min={0}
                        step="any"
                        value={formData.hourlyPrice}
                        onChange={(e) => setFormData({ ...formData, hourlyPrice: e.target.value })}
                    />

                    <FormField
                        className="sm:col-span-6"
                        label="Assigned dealers"
                        help={
                            availableDealers.length === 0 && !dealersLoading
                                ? 'No dealers are assigned to this club yet.'
                                : 'Only one assigned dealer can be logged in at a time.'
                        }
                    >
                        {dealersLoading ? (
                            <div className="flex items-center gap-2 h-10 text-sm text-cm-muted">
                                <Spinner size={14} /> Loading dealers…
                            </div>
                        ) : availableDealers.length > 0 ? (
                            <div className="flex flex-wrap gap-1.5" role="group" aria-label="Assigned dealers">
                                {availableDealers.map((dealer) => {
                                    const selected = formData.assignedDealers.includes(dealer._id)
                                    return (
                                        <button
                                            key={dealer._id}
                                            type="button"
                                            aria-pressed={selected}
                                            onClick={() => handleToggleDealer(dealer)}
                                            className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-full border text-[13px] font-semibold transition-colors ${selected
                                                ? 'border-cm-gold bg-cm-gold-soft text-cm-gold-text'
                                                : 'border-cm-border bg-cm-surface-2 text-cm-muted hover:text-cm-text'
                                                }`}
                                        >
                                            {selected ? <Check size={13} aria-hidden="true" /> : <Plus size={13} aria-hidden="true" />}
                                            {dealer.name || dealer.email}
                                        </button>
                                    )
                                })}
                            </div>
                        ) : null}
                    </FormField>

                    <TextInput
                        className="sm:col-span-2"
                        label="Total seats"
                        type="number"
                        min={MIN_TABLE_SEATS}
                        max={MAX_TABLE_SEATS}
                        step={1}
                        value={formData.totalSeats}
                        onChange={(e) => setFormData({ ...formData, totalSeats: e.target.value })}
                        error={seatsInvalid ? `Must be ${MIN_TABLE_SEATS}–${MAX_TABLE_SEATS}` : undefined}
                        help={`Allowed: ${MIN_TABLE_SEATS}–${MAX_TABLE_SEATS}`}
                    />
                    <TextInput
                        className="sm:col-span-2"
                        label="Small blind"
                        type="number"
                        value={formData.smallBlind}
                        onChange={(e) => setFormData({ ...formData, smallBlind: e.target.value })}
                    />
                    <TextInput
                        className="sm:col-span-2"
                        label="Big blind"
                        type="number"
                        value={formData.bigBlind}
                        onChange={(e) => setFormData({ ...formData, bigBlind: e.target.value })}
                    />
                    <TextInput
                        className="sm:col-span-3"
                        label="Min buy-in"
                        type="number"
                        prefix="₹"
                        value={formData.minBuyIn}
                        onChange={(e) => setFormData({ ...formData, minBuyIn: e.target.value })}
                    />
                    <TextInput
                        className="sm:col-span-3"
                        label="Max buy-in"
                        type="number"
                        prefix="₹"
                        value={formData.maxBuyIn}
                        onChange={(e) => setFormData({ ...formData, maxBuyIn: e.target.value })}
                    />

                    {editMode && (
                        <SelectInput
                            className="sm:col-span-3"
                            label="Status"
                            value={formData.status}
                            onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                            options={TABLE_STATUSES}
                        />
                    )}
                </div>
            </Modal>
        </>
    )
}

export default TableManagement
